/**
 * THE ACTION LAYER — TagSet + InputBuffer + the melee ActionSystem FSM + one fire() choke
 * point. The Option B consolidation, landed at last (2026-07-21).
 *
 * Before this module, main.ts and combat-lab.ts each carried a hand-mirrored copy of the
 * whole melee decision layer (LMB routing, stinger machine, High-Time, jump-follow, buffer,
 * dodge hand-offs, soft-lock magnetism, the swept-contact pass) — every feature was written
 * twice and two real bugs (the low-FPS buffered-light launcher cancel, the stinger/buffer
 * interplay) lived exactly in the mirroring. Now BOTH drive this one system through a small
 * host interface; the lab stays a faithful harness by construction.
 *
 * Division of labor:
 *   - COMBAT_ACTIONS (shared) stays the authoritative DATA (timings, windows, damage, arcs).
 *   - Avatar stays the CLIP layer (starters own mixer actions; the one fixed-tick action
 *     clock lives there — stepAction).
 *   - THIS system is the DECISION layer: what fires when, what buffers, what cancels, and
 *     the swept-contact resolution. All bookkeeping every attack shares (hit-set reset,
 *     suck-to-target, PvP punch discipline, audio hook) funnels through fire().
 *   - The HOST is the world adapter: where targets come from and what a confirmed hit does
 *     (net claims + fx in main; the dummy sim in the lab).
 *
 * Two entry points, matching the two call sites main.ts always had:
 *   tick(dt)        — input-gated decisions (called where the old input block lived).
 *   contactTick(dt) — the action clock + punch-close + High-Time rise + swept contact
 *                     (called un-gated, where stepAction always ran — a swing in flight
 *                     keeps sweeping even with the map open).
 */
import * as THREE from "three";
import {
  BLADE_BASE_DMG, COMBAT_ACTIONS, HIT_ARCS, SWEPT_MELEE, SWORD_COMBO_CHAIN, SWORD_AIR_CHAIN, activeT,
  type CombatActionId,
} from "@hyperlight/shared";
import type { CharacterController } from "../character/character";
import type { Avatar } from "../character/avatar";
import type { Input } from "../engine/input";
import { SoftLock } from "./softLock";
import { pickAssistTarget } from "./targetAssist";
import { sweepMeleeTargets, type MeleeTarget, type BodyFrame } from "./meleeContact";
import { BLADE_MOVE_SPEED, BLADE_JOG_SPEED } from "./bladeLocomotion";

// ── Tuning (single-sourced here; the old main/lab duplicates are gone) ──────────────────
/** Both attack movement tiers use the Powerful Sword Run octagon. */
/** The riposte reaches this far (m, tangent) — LMB at a STAGGERED enemy inside it fires
 *  the execution instead of the combo step. */
const RIPOSTE_RANGE = 4.5;
/** No-assist swing step impulse (m/s) PER COMBO HIT — the Light A→D movement profile
 *  (user call 2026-07-21): A steps a tiny bit, B holds ground, C steps small, D steps
 *  medium into its slash. Indexed by combo step; suck-to-target overrides when a target
 *  is picked (attackLungeAt sizes the travel to arrive, which is the point). */
/** No-target STRIKE STEP per light-chain hit (m) — the distances the old
 *  5/0/10/18 m/s impulses coasted, now authored steps; B takes a small one. */
const SWORD_STEP_DIST: readonly number[] = [.45, .25, .9, 1.6];
/** A swing's step when the stick directs it (sidestep / back-off / free) (m). */
const SWORD_STEER_STEP = .85;
/** Stick magnitude that counts as directing a swing. */
const STEER_STICK = .35;
/** How fast held movement still carries the body through a blade swing (m/s). */
const SWORD_STRIKE_DRIFT = 1.2;
/** Press buffer (s) — sized to cover the longest chain wait so a mash is never eaten. */
const SWORD_INPUT_BUFFER = 0.6;
/** Jump-follow window (s) after a launcher CONNECT. */
const LAUNCH_FOLLOW_S = 0.9;
/** High-Time rise impulse (m/s) — see character.highTimeRise; enemy launch (11.4) crests with it. */
const HIGH_TIME_RISE = 11.3;
/** Soft-lock orbit magnetism: proportional ease (rate ∝ bearing error) under a hard cap.
 *  This is CAMERA-LOCKED movement, not a body snap (user call 2026-07-22, twice): the
 *  heading (= the camera) gently recenters onto the selected enemy while the player walks
 *  around normally in the strafe stance — the 1H focus feel, now shared verbatim by the
 *  2H circling mode (greatsword.ts imports these). A hard 8 rad/s face was tried and
 *  REJECTED — it whipped the camera; the mouse must always win. */
export const SOFT_LOCK_TURN = 2.4;
export const SOFT_LOCK_EASE = 12;
// STINGER — the soft-lock gap closer (leap + flight-retimed clip; the slash IS the strike).
const STINGER_MIN_DIST = 4.2;
/** Forward stick lean that arms the gap-closer (was: holding Shift). Well past a stray nudge, so
 *  standing still — or strafing around the lock — still swings in place. */
const STINGER_FWD_MIN = 0.35;
const STINGER_SHORT_MAX = 9; // ≤ this: the close rides the PS Combo C3 (sd_shortdash)
const STINGER_ARRIVE_DIST = 2.3;
const STINGER_MAX_S = 1.9;
/** Starting the gpound claims the locked victim inside this range (dragged down together). */
const GPOUND_GRAB_DIST = 5;
/** The slam's HOLD threshold (s): airborne LMB held this long fires the GroundPound --
 *  taps never do (user call 2026-07-23). */
const GPOUND_HOLD_S = 0.28;

/** Remaining stinger flight seconds for a tangent distance (cruise at 28 m/s + the linear
 *  ease to 8 across the last 6 m — the ease integrates to (R/Δv)·ln(v/v0)). Feeds
 *  avatar.retimeStinger so the dash clip's impact frame lands EXACTLY at arrival. */
export const stingerFlightS = (d: number): number => {
  const easeSpan = Math.min(Math.max(0, d - STINGER_ARRIVE_DIST), 6);
  const vAtSpan = 8 + ((28 - 8) * easeSpan) / 6;
  return Math.max(0, d - STINGER_ARRIVE_DIST - 6) / 28 + (6 / (28 - 8)) * Math.log(vAtSpan / 8);
};

/** TAG SET — the frame's combat predicates, derived once per tick instead of ad-hoc boolean
 *  spaghetti at every gate. Rebuilt by ActionSystem.tick(); read anywhere via has(). */
export type CombatTag =
  | "melee"       // blade equipped + usable
  | "airborne"
  | "striking"    // a full sd_ strike / flinch is playing (avatar.isMeleeStriking)
  | "rolling"
  | "stinger"     // gap-closer homing in flight
  | "linger"      // stinger arrival slash/tail playing out
  | "focus"       // RMB focus hold (melee)
  | "comboOpen"
  | "attacking";  // an ARCED action is live (damage can happen) — drives the swing streak

export class TagSet {
  private readonly set = new Set<CombatTag>();
  clear(): void {
    this.set.clear();
  }
  add(t: CombatTag): void {
    this.set.add(t);
  }
  has(t: CombatTag): boolean {
    return this.set.has(t);
  }
}

/** INPUT BUFFER — one place for "the press arrived early; keep trying until the window
 *  opens or the buffer dies". Replaces the two hand-rolled swordBufferT copies. */
export class InputBuffer {
  private t = 0;
  arm(seconds = SWORD_INPUT_BUFFER): void {
    this.t = seconds;
  }
  clear(): void {
    this.t = 0;
  }
  get live(): boolean {
    return this.t > 0;
  }
  /** One consume attempt per tick: `fire` returns true when it actually fired (buffer
   *  clears) — false decays the buffer by dt and keeps it pending. */
  consume(dt: number, gate: boolean, fire: () => boolean): void {
    if (this.t <= 0 || !gate) return;
    if (fire()) this.t = 0;
    else this.t = Math.max(0, this.t - dt);
  }
}

/** What a hit claim needs from the world. `launch` rides it (± = pop/slam, server-capped);
 *  `heavy` picks the feel tier (hitstop/rumble). */
export interface ActionHost {
  character: CharacterController;
  avatar: Avatar;
  input: Input;
  softLock: SoftLock;
  /** Blade equipped + usable this tick (main: mode==="blade" && !building; lab: true). */
  meleeActive(): boolean;
  /** Camera world forward (assist cones + the lock). */
  cameraForward(out: THREE.Vector3): THREE.Vector3;
  /** Attack-assist candidates (live refs); also used for locking by default. */
  targets(): { id: number; pos: THREE.Vector3; staggered?: boolean }[];
  /** Optional wider focus selection. Seeing a target need not authorize a lunge. */
  lockTargets?(): { id: number; pos: THREE.Vector3 }[];
  /** Visible gap-closer targets, with positions at the desired foot height. */
  leapTargets?(): { id: number; pos: THREE.Vector3 }[];
  /** Swept-contact candidates (view-anchored). */
  meleeTargets(): MeleeTarget[];
  /** Planet-local frame for the sweep (null = system space; see meleeContact.ts). */
  bodyFrame(): BodyFrame | null;
  /** Capsule radius the sweep tests targets at. */
  targetRadius: number;
  /** Live blade damage (0 = a hit can't be claimed this tick). */
  bladeDamage(): number;
  /** A CONFIRMED hit: apply the claim + all fx. `actionId` names the striking action so
   *  the wire claim carries it (the server's per-action damage cap — the riposte's 3.5×). */
  onHit(id: number, pos: THREE.Vector3, dmg: number, launch: number | undefined, heavy: boolean, actionId?: CombatActionId): void;
  /** A swing/dash was fired (audio hook: whoosh + combat-music nudge). */
  onSwing(): void;
  /** THE PARRY fired (RMB tap): the host opens its parry window from the def's active
   *  window (parryFrom/Until wall-clock + the streamed `py`/`iv` flags ride it). */
  onParry?(def: { startupMs: number; activeMs: number }): void;
  /** PvP punch discipline (main). Omit where there is no wire (the lab). */
  sendPunch?(id: CombatActionId): void;
}

export class ActionSystem {
  readonly tags = new TagSet();
  readonly buffer = new InputBuffer();
  /** F belongs to interact everywhere now (the lock retargets by AIM, not F-cycle). Kept
   *  as an always-true seam so the interact rungs' gate survives any future F claim. */
  focusFree = true;

  private stingerT = 0;
  private stingerId: number | null = null;
  private lingerT = 0;
  private followT = 0;
  private attackMode = false;
  private punchPending: CombatActionId | null = null;
  private airEvadeT = 0;
  private gpoundHoldT = 0;
  private gpoundArmed = false;
  private readonly hitSet = new Set<number>();
  private readonly _fwd = new THREE.Vector3();
  private readonly _dir = new THREE.Vector3();
  private readonly _cross = new THREE.Vector3();
  private readonly _hits: MeleeTarget[] = [];

  constructor(private readonly host: ActionHost) {}

  /** An arced action or the stinger is live — the swing-streak gate. */
  attacking(): boolean {
    return this.tags.has("attacking") || this.stingerT > 0 || this.lingerT > 0;
  }

  /** A mode/weapon swap cancels the swing — no punch may go out (see sendPending). */
  cancelPendingPunch(): void {
    this.punchPending = null;
  }

  /** Release the hold, including buffered strikes and movement owned by melee.
   *  The avatar crossfades back to locomotion while the crystals unfold. */
  releaseAttackMode(): void {
    const {character,avatar,softLock}=this.host;
    this.buffer.clear();this.tags.clear();this.punchPending=null;
    this.stingerT=this.lingerT=this.followT=this.airEvadeT=this.gpoundHoldT=0;
    this.gpoundArmed=false;this.attackMode=false;
    avatar.endBladeAttack();avatar.setSwordFocus(false);softLock.drop();
    character.stingerEnd();character.setAirFocus(null);character.airOrbitT=0;
    character.actionAirLock=false;character.evadeReady=false;
    character.strafeLock=false;character.combatStance=false;
  }

  private get weaponReady(): boolean {
    return !this.host.avatar.crystalBladeEnabled || this.host.avatar.crystalBladeReady;
  }

  /** The soft-locked candidate (tangent distance), or null. The lock WINS the attack
   *  assist — an explicit target IS the intent, cone rules waived. */
  private lockAssist(): { id: number; pos: THREE.Vector3; dist: number } | null {
    const id = this.host.softLock.targetId;
    if (id === null) return null;
    const t = this.host.targets().find((x) => x.id === id);
    if (!t) return null;
    const c = this.host.character;
    this._dir.copy(t.pos).sub(c.pos);
    this._dir.addScaledVector(c.up, -this._dir.dot(c.up));
    return { id, pos: t.pos, dist: this._dir.length() };
  }

  private leapPick(id = this.host.softLock.targetId): {id: number; pos: THREE.Vector3; dist: number; height: number} | null {
    if (id === null) return null;
    const target = (this.host.leapTargets?.() ?? this.host.targets()).find(t => t.id === id);
    if (!target) return null;
    const c = this.host.character;
    this._dir.copy(target.pos).sub(c.pos);
    const height = this._dir.dot(c.up);
    this._dir.addScaledVector(c.up, -height);
    return {id, pos: target.pos, dist: this._dir.length(), height};
  }

  private sendPending(): void {
    if (this.punchPending && this.host.sendPunch) this.host.sendPunch(this.punchPending);
    this.punchPending = null;
  }

  /** A STAGGERED enemy in riposte reach, or null: the lock wins; else the nearest
   *  staggered candidate (its dizzy IS the invitation — no cone needed). */
  private ripostePick(): { id: number; pos: THREE.Vector3; dist: number } | null {
    const c = this.host.character;
    const lk = this.lockAssist();
    const cands = this.host.targets();
    const staggered = (id: number): boolean => cands.find((t) => t.id === id)?.staggered === true;
    if (lk && lk.dist <= RIPOSTE_RANGE && staggered(lk.id)) return lk;
    let best: { id: number; pos: THREE.Vector3; dist: number } | null = null;
    for (const t of cands) {
      if (!t.staggered) continue;
      this._dir.copy(t.pos).sub(c.pos);
      this._dir.addScaledVector(c.up, -this._dir.dot(c.up));
      const dist = this._dir.length();
      if (dist <= RIPOSTE_RANGE && (!best || dist < best.dist)) best = { id: t.id, pos: t.pos, dist };
    }
    return best;
  }

  /** THE ONE FIRE CHOKE POINT — every attack start funnels here: the avatar starter, then
   *  the bookkeeping each of them used to duplicate (audio, per-swing hit-set reset, stand
   *  up, suck-to-target/step, punch flush + pend). Returns false when the starter refused
   *  (mid-commit) — callers decide whether to buffer. */
  /** Seconds from the start of the action just fired to the middle of its hit
   *  window — where its strike step is fastest (the blade meets the target
   *  while the body is still travelling into it). */
  private contactS(): number {
    const act = this.host.avatar.activeAction();
    const def = act ? COMBAT_ACTIONS[act.id] : undefined;
    return def ? Math.max(.08, (def.startupMs + def.activeMs / 2) / 1000) : .16;
  }

  private fire(kind: "swing" | "launcher" | "aerial" | "gpound" | "riposte", riposteAt?: { id: number; pos: THREE.Vector3; dist: number }): boolean {
    const { avatar, character } = this.host;
    if(!this.weaponReady)return false;
    let id: CombatActionId | null = null;
    let step = -1;
    if (kind === "riposte") {
      if (!avatar.swordRiposte()) return false;
      id = "sword_riposte";
    } else if (kind === "launcher") {
      if (!avatar.swordLauncher()) return false;
      id = "sword_launcher";
    } else if (kind === "gpound") {
      if (!avatar.swordGpound()) return false;
      id = "sword_gpound";
    } else if (kind === "swing") {
      step = avatar.swordSwing();
      if (step < 0) return false;
      id = SWORD_COMBO_CHAIN[step] ?? null;
    } else {
      step = avatar.swordAerial();
      if (step < 0) return false;
      id = SWORD_AIR_CHAIN[step] ?? null;
    }
    this.host.onSwing();
    this.hitSet.clear();
    character.crouching = false; // attacking STANDS the player up (the slashes are standing clips)
    if (kind === "gpound") {
      character.slamDown(10); // authored: GroundPound_RM descends 2 m in ~0.3 s — match it
      // DRAG THE VICTIM STRAIGHT DOWN WITH YOU: claim the slam on the locked target the
      // moment the drop starts — both spike floorward together — instead of hoping the
      // blade contacts a body hanging ABOVE the descent path. The sweep covers lockless play.
      const gp = this.lockAssist();
      const dmg = this.host.bladeDamage();
      if (gp && gp.dist < GPOUND_GRAB_DIST && dmg > 0) {
        this.host.onHit(gp.id, gp.pos, dmg, -12, true);
        this.hitSet.add(gp.id); // the sweep must not double-claim this swing
      }
    } else if (kind === "riposte") {
      // THE EXECUTION: steer onto the staggered body — its dizzy is the invitation.
      if (riposteAt) character.attackLungeAt(riposteAt.pos, riposteAt.dist, this.contactS());
    } else {
      if (kind === "aerial") character.aerialHop(); // the air rave hangs — see character.ts
      // SUCK-TO-TARGET: a swing near an enemy steers ONTO it (auto-face + arrival-sized
      // lunge). No target → the classic fixed step along the input (finisher further).
      // THE DIRECTED STEP (MH Wilds focus): the stick picks where the swing
      // travels. Neutral or toward the target → step onto it (suck-to-target);
      // held sideways or back → the body still faces the target but the swing
      // sidesteps / backs off along the stick. No target: along the stick, or
      // the chain's own short step when neutral.
      const pick = this.lockAssist()
        ?? pickAssistTarget(character.pos, character.up, this.host.cameraForward(this._fwd), this.host.targets());
      const md = character.moveDirLocal(), stick = Math.hypot(md.fwd, md.right);
      if (pick && kind === "swing" && stick > STEER_STICK && character.onGround && character.stickToward(pick.pos) < .5) {
        character.faceTarget(pick.pos);
        character.attackStepLocal(md.fwd, md.right, SWORD_STEER_STEP, this.contactS());
      } else if (pick) character.attackLungeAt(pick.pos, pick.dist, this.contactS());
      else if (kind === "swing") {
        const distance = stick > STEER_STICK ? SWORD_STEER_STEP : SWORD_STEP_DIST[step] ?? 0;
        character.attackStepLocal(md.fwd, md.right, distance, this.contactS());
      }
    }
    // Chaining PROVES the previous swing's active window closed (THE CANCEL INVARIANT) —
    // its punch goes on the wire now; this swing's own punch pends until ITS window closes,
    // and never goes out if it gets cancelled first. The RIPOSTE never pends: players
    // can't be staggered (v1), so its 3.5× must not ride the PvP wire.
    this.sendPending();
    this.punchPending = kind === "riposte" ? null : id;
    return true;
  }

  /** Start a committed gap-closer toward `pick`, homing to its foot height. */
  private startStinger(pick: { id: number; pos: THREE.Vector3; dist: number }, airborne: boolean): void {
    const { character, avatar } = this.host;
    this.stingerT = STINGER_MAX_S;
    this.stingerId = pick.id;
    this.gpoundArmed = false; this.gpoundHoldT = 0;
    character.stingerSteer(pick.pos, airborne ? 0 : stingerFlightS(pick.dist)); // leap sized to LAND at the target
    avatar.setStinger(true, pick.dist <= STINGER_SHORT_MAX ? "sd_shortdash" : airborne ? "sd_airdash" : "sd_dash");
    this.host.onSwing();
    this.buffer.clear();
  }

  /** INPUT-GATED decisions — call once per fixed tick where player input is live. */
  tick(dt: number): void {
    const { character, avatar, input, softLock } = this.host;
    const selected=this.host.meleeActive(),held=selected&&input.isButtonDown(2)&&character.aiming;
    if(!held&&(this.attackMode||selected))this.releaseAttackMode();
    if(selected&&!held){
      // Hurt reactions still own hitstun; releasing attack cannot cancel damage reactions.
      character.combatStance=avatar.meleeStrikeRoots();
      character.combatSpeed=character.combatStance?0:BLADE_MOVE_SPEED;
      return;
    }
    this.attackMode=held;
    if(character.diveLandingLocked){
      // Do not buffer a dodge/lunge that would steal the planted landing beat.
      this.buffer.clear();this.tags.clear();this.punchPending=null;
      this.stingerT=0;this.lingerT=0;this.followT=0;this.airEvadeT=0;
      this.gpoundHoldT=0;this.gpoundArmed=false;
      character.stingerEnd();character.actionAirLock=false;character.evadeReady=false;
      avatar.cancelAction();avatar.setBlocking(false);
      return;
    }
    const melee = held;
    const md = character.moveDirLocal();
    // Acquire before routing this press: RMB + forward + LMB can begin together.
    if (!input.isButtonDown(2)) softLock.drop();
    softLock.deselectTick(dt, input.isDown("KeyF"), input.wasPressed("Escape"));
    const locked = softLock.update(dt, character.pos, character.up, this.host.cameraForward(this._fwd), melee,
      this.host.lockTargets?.() ?? this.host.targets());
    /** A selection is live for this input tick. */
    const lockedOn = melee && softLock.targetId !== null;
    /** Shift = the SPEED tier (jog/lunge intent): it gates the stinger and forbids the
     *  parry (user calls 2026-07-22). */
    const shift = input.isDown("ShiftLeft") || input.isDown("ShiftRight");

    // ── Tags: this frame's predicates, one derivation. ──
    this.tags.clear();
    if (melee) this.tags.add("melee");
    if (!character.onGround) this.tags.add("airborne");
    if (avatar.isMeleeStriking()) this.tags.add("striking");
    if (avatar.isRolling()) this.tags.add("rolling");
    if (this.stingerT > 0) this.tags.add("stinger");
    if (this.lingerT > 0) this.tags.add("linger");
    if (melee && character.aiming) this.tags.add("focus");
    if (avatar.comboOpen()) this.tags.add("comboOpen");
    const act0 = avatar.activeAction();
    if (act0 && COMBAT_ACTIONS[act0.id]?.arc !== undefined) this.tags.add("attacking");
    // THE DOWN-THRUST OWNS THE BODY (user call 2026-07-22): while the gpound slam is in
    // flight, Space may not dodge/jump/jet out of it — the dive commits to touchdown.
    //
    // ⚠⚠ `&& !character.onGround` IS A LATCH-BREAKER, NOT A REFINEMENT. This lock is the only
    // thing between the player and a dead jump key, and the action id it reads can go STALE: the
    // action clock only advances in `contactTick`, which lives inside main.ts's foot-mode block,
    // so mounting a vehicle / docking / any mode change mid-slam freezes `activeAction()` on
    // `sword_gpound` forever. Back on foot the lock re-latched every tick and Space was dead for
    // the rest of the session — through weapon switches, since this line is not melee-gated (user
    // report 2026-08-03: "sometimes jump gets locked out afterwards and the player can no longer
    // jump, even if they switch weapons"). A slam is airborne BY DEFINITION and commits to
    // touchdown, so standing on the ground is proof it is over whatever the clock believes.
    character.actionAirLock = act0?.id === "sword_gpound" && !character.onGround;

    // ── LMB routing. ──
    if (!this.tags.has("airborne")) {
      this.gpoundArmed = false; // the slam gesture is airborne-only; grounded clears it
      this.gpoundHoldT = 0;
    }
    if (melee && this.tags.has("airborne")) {
      // THE GROUND POUND IS HOLD-ONLY (user call 2026-07-23: the air combo must not end in
      // the slam by default). Taps juggle A <-> B; keeping LMB DOWN for GPOUND_HOLD_S fires
      // the slam -- retried each tick past the threshold, so it chains in at the current
      // air hit's cancel point (the hold-through-swing read, like High Time).
      // The hold must START in the air: a High-Time hold carried up from the launcher
      // (LMB never released) must not auto-slam — an airborne PRESS arms the gesture.
      if (input.wasButtonPressed(0)) this.gpoundArmed = true;
      this.gpoundHoldT = this.gpoundArmed && input.isButtonDown(0) ? this.gpoundHoldT + dt : 0;
      if (this.gpoundHoldT >= GPOUND_HOLD_S && this.stingerT <= 0
        && avatar.activeAction()?.id !== "sword_gpound" && this.fire("gpound")) {
        this.gpoundHoldT = 0;
        this.gpoundArmed = false;
      }
      // AIR STINGER: locked + LMB at range → home on the PS CommonAttack (short closes ride
      // the finisher clip); otherwise the AERIAL STRING fires on the press (A → B → slam).
      const airPick =
        this.weaponReady && input.wasButtonPressed(0) && (shift || md.fwd > STINGER_FWD_MIN) && this.stingerT <= 0 && !this.tags.has("striking") ? this.leapPick() : null;
      if (airPick && airPick.dist > STINGER_MIN_DIST) this.startStinger(airPick, true);
      else {
        if(input.wasButtonPressed(0))this.buffer.arm();
        this.buffer.consume(dt,this.stingerT<=0,()=>this.fire("aerial"));
      }
    } else if (melee) {
      // THE RIPOSTE — LMB with a STAGGERED (parried) enemy in reach fires the execution
      // in place of the combo step. The lock wins the pick; lockless play takes the
      // nearest staggered body. Checked FIRST: a stagger in range IS the intent.
      const riposteTarget = input.wasButtonPressed(0) && this.stingerT <= 0 && !this.tags.has("striking")
        ? this.ripostePick()
        : null;
      // THE STINGER — locked + moving FORWARD + LMB at range: leap to them, the dash leads into a
      // validated slash at arrival. From neutral only.
      //
      // The trigger used to be SHIFT (a "speed-tier verb"), retired on user call 2026-08-03:
      // "remove the shift requirement to do the gap-closing attack when moving forward and
      // attacking while locked on". PUSHING TOWARD the enemy already IS the intent to close, and
      // it composes with the Shift change below — Shift no longer breaks the lock, so it would
      // have been a speed modifier and a verb trigger at once. Standing still and pressing still
      // swings in place, which is what kept the old gate honest.
      const stingerPick =
        this.weaponReady && !riposteTarget && input.wasButtonPressed(0) && md.fwd > STINGER_FWD_MIN
          && this.stingerT <= 0 && !this.tags.has("striking") && character.onGround
          ? this.leapPick()
          : null;
      if (riposteTarget) {
        this.fire("riposte", riposteTarget);
      } else if (stingerPick && stingerPick.dist > STINGER_MIN_DIST) {
        this.startStinger(stingerPick, false);
      }
      // THE LAUNCHER (High Time — focus + BACK + press) pops the target instead of chaining.
      else if (character.aiming && md.fwd < -0.35 && input.wasButtonPressed(0)) {
        if (!this.fire("launcher") && avatar.activeAction()?.id !== "sword_launcher") {
          // Mid-commit → buffer like any chain press — EXCEPT while the LAUNCHER itself is
          // committing: at low frame rates one press spans several accumulated ticks, and
          // the re-fired edge was buffering a light that CANCELLED the pop at its cancel
          // window. The High-Time beat owns that window.
          this.buffer.arm();
        }
      } else if (input.wasButtonPressed(0)) this.buffer.arm();
      // A press mid-STINGER buffers but does NOT consume until the dash resolves AND its
      // arrival slash beat has played — the buffered press then chains off the tail.
      this.buffer.consume(dt, this.stingerT <= 0 && this.lingerT <= 0.35, () => this.fire("swing"));
    }

    // ── STINGER machine: home each tick; arrival begins the validated sword action. ──
    if (this.stingerT > 0) {
      this.stingerT -= dt;
      const lk = melee ? this.leapPick(this.stingerId) : null;
      if (!lk || this.stingerT <= 0) {
        this.stingerT = 0;
        this.lingerT = 0;
        character.stingerEnd();
        avatar.setStinger(false);
      } else if (lk.dist <= STINGER_ARRIVE_DIST && Math.abs(lk.height) <= 1.25) {
        this.stingerT = 0; this.lingerT = 0;
        character.stingerEnd();
        avatar.setStinger(false);
        // The arrival is a real sword action. Both world contact and the race
        // server validate its swept arc; a visual dash alone cannot deal damage.
        this.fire(character.onGround ? "swing" : "aerial");
      } else {
        character.stingerSteer(lk.pos);
        avatar.retimeStinger(stingerFlightS(lk.dist)); // impact frame → exactly at arrival
      }
    }
    // LINGER — the arrival slash/recovery. A chained swing cancels; movement only cancels
    // the TAIL (the slash follow-through is protected — lingerT ≤ 0.2).
    if (this.lingerT > 0) {
      this.lingerT -= dt;
      const moveOut = this.lingerT <= 0.2 && Math.hypot(md.fwd, md.right) > 0.2;
      if (this.lingerT <= 0 || this.tags.has("striking") || moveOut) {
        this.lingerT = 0;
        avatar.setStinger(false);
      }
    }

    // ── JUMP-FOLLOW (knock up → jump after them): Space inside the window converts the
    //    jump into the High-Time rise. A directional Space (a dodge) always wins. ──
    if (this.followT > 0) {
      this.followT -= dt;
      if (input.wasPressed("Space") && !character.pendingDodge && !character.pendingAirDodge && !character.onGround) {
        character.highTimeRise(HIGH_TIME_RISE);
        avatar.openAirRave();
        this.followT = 0;
      }
    }

    // ── Dodge hand-offs (the controller decided; play the clip). ──
    if (character.pendingAirDodge) {
      const side = character.pendingAirDodge;
      character.pendingAirDodge = null;
      if (melee) {
        // 1H AIR EVADE (user call 2026-07-23): NO dodge clip — CIRCLE the locked target
        // horizontally in the aerial float (sd_air_idle). Lockless keeps the controller's
        // side impulse, float included. The i-frames/cooldown were already armed.
        const lk = this.lockAssist();
        if (lk) character.airOrbitStart(lk.pos, side);
        avatar.setAirEvade(true);
        this.airEvadeT = 0.5; // matches AIR_ORBIT_S; releases the float below
      } else {
        avatar.airDodge(side); // non-melee stances keep the UAL flip
      }
      this.host.onSwing(); // the whoosh
    }
    // AIR-EVADE maintenance: steer the circle at the live lock; release the float when the
    // orbit ends or the feet touch down.
    if (this.airEvadeT > 0) {
      this.airEvadeT -= dt;
      const lk = this.lockAssist();
      if (lk) character.airOrbitSteer(lk.pos);
      if (this.airEvadeT <= 0 || character.onGround) {
        this.airEvadeT = 0;
        avatar.setAirEvade(false);
      }
    }
    if (character.pendingDodge) {
      // The SSH ROLL family — every tier (user call 2026-07-23; the walk-tier strafe-jump
      // detour is reverted; the sd_dodge_* dash stays unrouted).
      if (melee) avatar.swordDodge(character.pendingDodge);
      // Kickbox rolls are 4-way — a back-diagonal falls back to the pure back roll.
      else avatar.evadeRoll(character.pendingDodge === "bl" || character.pendingDodge === "br" ? "b" : character.pendingDodge);
      character.pendingDodge = null;
    }
    // SEAMLESS ROLL EXIT: past the travel window, held movement cancels the recovery tail.
    if (this.tags.has("rolling") && character.dashT <= 0 && Math.hypot(md.fwd, md.right) > 0.2) {
      avatar.cancelRollRecovery();
    }
    // COMBO-END RECOVERY EXIT (same contract): held movement runs straight out of a dropped
    // combo's Sword_Regular_X_Rec flourish instead of gliding through it.
    if (avatar.inComboRec() && Math.hypot(md.fwd, md.right) > 0.2) {
      avatar.cancelComboRec();
    }
    // MOVEMENT CANCEL (DMC): once a swing's damage is done (its cancel point),
    // a held direction hands the body straight back to movement — the rest of
    // the swing's follow-through is optional. An attack press still chains.
    if (melee && character.onGround && Math.hypot(md.fwd, md.right) > STEER_STICK
      && !this.buffer.live && !input.isButtonDown(0) && this.stingerT <= 0 && avatar.swordMoveCancelable()) {
      avatar.cancelSwordStrike();
      character.endStrikeStep();
    }

    // ── Speed/stance feedback to the controller. A STICKY LOCK holds the "face this
    //    enemy" stance without the RMB hold (user call 2026-07-22): strafe footwork +
    //    the focus pace, exactly as if the focus were held. (targetId here is at most
    //    one tick stale — the lock updates just below.) ──
    character.combatSpeed = avatar.combatGroundSpeed();
    // STRIKE CONTROL: a blade swing keeps a slow carry on held movement, tracks
    // the reticle/target during its windup when armed, and holds its line through
    // the hit window (an unaimed swing holds from the start — the auto-face aimed it).
    if (melee && avatar.meleeStrikeRoots()) {
      const act = avatar.activeAction(), def = act ? COMBAT_ACTIONS[act.id] : undefined;
      const hitting = !!def && def.arc !== undefined && act!.elapsedMs >= def.startupMs && act!.elapsedMs < def.startupMs + def.activeMs;
      character.setStrikeControl(SWORD_STRIKE_DRIFT, hitting || !(character.aiming || lockedOn));
    }
    if (melee) {
      character.combatStance = this.tags.has("striking") || character.aiming || lockedOn;
      // Only FULL-BODY strikes root — the top-only parry deflection keeps walking
      // (user call 2026-07-22: an RMB tap must not stop movement).
      character.combatSpeed = avatar.meleeStrikeRoots() ? 0
        : shift ? BLADE_JOG_SPEED : BLADE_MOVE_SPEED;
    }
    // The body FACES the enemy while selected (the aiming-facing rule without the hold):
    // heading magnetism recenters the camera onto the target below; this makes the body
    // follow it, so movement strafes around the faced enemy.
    character.strafeLock = lockedOn;
    // THE EVADE ROLL is back on EVERY movement tier (user call 2026-07-23, reverting the
    // walk-tier strafe-jump): a directional Space rolls whether walking or jogging — no
    // more jumping out of the lock. (The SSH sd_jump_* takeoffs are unrouted again;
    // neutral Space still jumps normally.)
    character.evadeReady = melee && (character.aiming || this.tags.has("comboOpen") || lockedOn);

    // ── SOFT LOCK: acquire on focus, then STICKY (survives the release — user call
    //    2026-07-22). AIM-SWITCH retargets while focused (a flick onto a neighbour hops
    //    the lock); DESELECT = move/face away (in update), F HELD a beat, or Esc.
    //    Orbit magnetism runs the whole selected stint (proportional ease — a fixed-rate
    //    chase chattered the camera, which IS the heading). ──
    if (locked) {
      this._dir.copy(locked.pos).sub(character.pos);
      this._dir.addScaledVector(character.up, -this._dir.dot(character.up));
      if (this._dir.lengthSq() > 1e-6) {
        this._dir.normalize();
        const ang = Math.acos(Math.max(-1, Math.min(1, character.heading.dot(this._dir))));
        // Cap keeps big errors gentle; a live STINGER triples it (a committed close).
        const step = Math.min(ang * (1 - Math.exp(-SOFT_LOCK_EASE * dt)), (this.stingerT > 0 ? 3 : 1) * SOFT_LOCK_TURN * dt);
        if (step > 1e-4) {
          this._cross.crossVectors(character.heading, this._dir);
          const sign = this._cross.dot(character.up) >= 0 ? 1 : -1;
          character.heading.applyAxisAngle(character.up, sign * step);
        }
      }
    }
    // AIR FOCUS (user call 2026-07-24): airborne, the LOCK owns the body's facing — evade,
    // air string, and the hang between them all keep the eyes on the enemy. On the ground the
    // shipped rule stands (strafeLock faces the magnetised heading), so this is air-only.
    character.setAirFocus(melee && locked && !character.onGround ? locked.pos : null);
    this.focusFree = true; // aim-switch freed KeyF — interact works mid-focus too
  }

  /** UN-GATED per-tick pass: the action clock, the PvP punch close, the High-Time rise and
   *  the swept blade contact. Call exactly where avatar.stepAction always ran — a swing in
   *  flight keeps sweeping (and paying) even when input is overlaid. */
  contactTick(dt: number): void {
    const { character, avatar } = this.host;
    // The COMBAT ACTION clock ticks HERE — fixed tick, not avatar.update(); and it must run
    // even with SWEPT_MELEE off, or every swing reads as forever mid-commit.
    avatar.stepAction(dt);
    if (!SWEPT_MELEE) return;
    const act = avatar.activeAction();
    const def = act ? COMBAT_ACTIONS[act.id] : null;
    const prevMs = act ? act.elapsedMs - dt * 1000 : 0;
    const activeClose = def ? def.startupMs + def.activeMs : 0;
    // PUNCH AT ACTIVE CLOSE (PvP): a pending punch whose swing is no longer live was
    // CANCELLED — drop it. Otherwise send it on the tick that CLOSES the active window:
    // the client has swept the identical [0,1] span the server will resolve.
    if (this.punchPending && (!act || act.id !== this.punchPending)) this.punchPending = null;
    if (this.punchPending && act && def && prevMs < activeClose && act.elapsedMs >= activeClose) {
      this.sendPending();
    }
    // HIGH TIME RISE — ALWAYS (user call 2026-07-22, was hold-LMB-gated): the launcher's
    // back+attack gesture IS the jump — the attacker rises with every High Time, fired at
    // the ACTIVE CLOSE (an open-fired 11.3 rise swept the blade over grounded targets).
    if (act && def && act.id === "sword_launcher"
      && prevMs < activeClose && act.elapsedMs >= activeClose) {
      character.highTimeRise(HIGH_TIME_RISE);
      avatar.openAirRave();
    }
    // THE SWEPT BLADE — interval overlap (NOT isActive) so the ticks tile [0,1] exactly;
    // resolved in the host's body frame (planet-local in main) so carry cancels.
    const overlaps = act !== null && def !== null && def.arc !== undefined
      && act.elapsedMs > def.startupMs && prevMs < activeClose;
    if (act && def && def.arc && overlaps) {
      const arc = HIT_ARCS[def.arc];
      // Per-action damage: the upgraded blade scaled by the action's table multiple
      // (lights ×1, the riposte ×3.5 — def.damage / BLADE_BASE_DMG). The claim carries
      // the action id so the server's per-action cap clears the big hits.
      const dmg = this.host.bladeDamage() * (def.damage / BLADE_BASE_DMG);
      if (arc && dmg > 0) {
        const hits = sweepMeleeTargets(
          arc, activeT(def, prevMs), activeT(def, act.elapsedMs),
          character.pos, character.quat, this.host.bodyFrame(),
          this.host.meleeTargets(), this.host.targetRadius, this.hitSet, this._hits,
        );
        const heavy = act.id === "sword_light_4" || act.id === "sword_riposte"; // finishers land heavy
        for (const d of hits) {
          this.hitSet.add(d.id);
          if (act.id === "sword_launcher") this.followT = LAUNCH_FOLLOW_S; // jump-follow opens
          this.host.onHit(d.id, d.pos, dmg, def.launch, heavy, act.id);
        }
      }
    }
  }
}
